import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import Meta from '../components/Meta';
import './Services.css';
import '../Animations.css';
import './Shared.css'; // Ensure this exists or remove if not needed, likely contains general styles
import fotoWeb from '../assets/foto_web.webp';
import fotoData from '../assets/foto_solution.webp';
import fotoMobile from '../assets/mobile_2.webp';
import fotoSolution from '../assets/office-multi-hero.webp';

function Services() {
  const { t } = useTranslation();
  const { id } = useParams();
  const serviceCategories = t('services_page.fallback_services', { returnObjects: true });
  const fotos = [fotoWeb, fotoData, fotoMobile, fotoSolution];

  useEffect(() => {
    if (id) {
      const serviceElement = document.getElementById(`service-${parseInt(id, 10) - 1}`);
      if (serviceElement) {
        serviceElement.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [id]);

  const selectedService = id ? serviceCategories[parseInt(id, 10) - 1] : null;

  return (
    <div className="services-page">
      <Meta
        title={selectedService ? `${selectedService.title} | Vorpoint` : t('services_page.meta_title')}
        description={selectedService ? selectedService.description : t('services_page.meta_description')}
      />
      <section className="modern-hero">
        <div className="hero-content">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {t('services_page.hero_title')}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {t('services_page.hero_subtitle')}
          </motion.p>
        </div>
      </section>

      {/* Digital Transformation Section */}
      <section className="digital-transformation">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="section-label">DIGITALE TRANSFORMATION</div>
          <h2>Ihre digitale Zukunft beginnt hier</h2>
          <p>
            Wir begleiten die digitale Transformation – von papierbasierten Abläufen hin zu modernen digitalen Systemen und Automatisierung.
          </p>
          <div className="transformation-grid">
            <motion.div className="transformation-card" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.1 }}>
              <div className="icon-wrapper"><span className="icon">⚡</span></div>
              <h3>Prozessoptimierung</h3>
              <p>Automatisierung repetitiver Aufgaben zur Steigerung der Effizienz</p>
              <Link to="/kontakt" className="learn-more">Mehr erfahren</Link>
            </motion.div>
            <motion.div className="transformation-card" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.2 }}>
              <div className="icon-wrapper"><span className="icon">🤖</span></div>
              <h3>Automation</h3>
              <p>KI-gestützte Lösungen für intelligente Geschäftsprozesse</p>
              <Link to="/kontakt" className="learn-more">Mehr erfahren</Link>
            </motion.div>
            <motion.div className="transformation-card" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.3 }}>
              <div className="icon-wrapper"><span className="icon">📈</span></div>
              <h3>Skalierbarkeit</h3>
              <p>Wachstumsorientierte Systeme, die mit Ihrem Unternehmen mitwachsen</p>
              <Link to="/kontakt" className="learn-more">Mehr erfahren</Link>
            </motion.div>
          </div>
        </motion.div>
      </section>

      <div className="full-width-container">
        {serviceCategories.map((category, index) => {
          const isEven = index % 2 === 0;
          const cardClassName = isEven ? 'service-card-services even' : 'service-card-services odd';

          return (
            <motion.div
              key={index}
              id={`service-${index}`}
              className={cardClassName}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <div className="image-container-services">
                <img src={fotos[index]} alt={category.title} />
              </div>
              <div className="text-container-services">
                <h2>{category.title}</h2>
                <h4>{category.description}</h4>
                <p>{category.service_motto}</p>
                <ul>
                  {category.services_list.map((service, sIndex) => (
                    <li key={sIndex}>{service}</li>
                  ))}
                </ul>
                <h3>{category.tech_stack_title}</h3>
                <p>{category.tech_stack}</p>
                <h3>{category.use_cases_title}</h3>
                <ul>
                  {category.use_cases.map((useCase, uIndex) => (
                    <li key={uIndex}>{useCase}</li>
                  ))}
                </ul>
                <h3>{category.ideal_for_title}</h3>
                <p>{category.ideal_for}</p>
                <Link to="/our_cost">
                  <motion.button
                    className="cost-button"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {t('cost_project')}
                  </motion.button>
                </Link>
              </div>
            </motion.div>
          );
        })}
      </div>

      <section className="final-cta-services">
        <motion.div
          className="final-cta-content-services"
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h2>{t('services_page.final_cta_title')}</h2>
          <p>{t('services_page.final_cta_text')}</p>
          <Link to="/our_cost">
            <motion.button
              className="cta-button-services"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {t('services_page.final_cta_button')}
            </motion.button>
          </Link>
        </motion.div>
      </section>
    </div>
  );
}

export default Services;